<template>
  <ValidationProvider
    :name="name"
    :rules="moddedRules"
    v-slot="{ valid, errors, invalid }"
    mode="eager"
    :class="className"
    slim
  >
    <md-field
      class="tooltip-container"
      :class="[{ 'md-error': invalid }, { 'md-valid': valid }]"
    >
      <slot> </slot>

      <slide-y-down-transition>
        <md-icon class="error" v-show="invalid">close</md-icon>
      </slide-y-down-transition>
      <span v-if="errors[0]" class="tooltip-text">{{ errors[0] }}</span>
    </md-field>
  </ValidationProvider>
</template>

<script>
import { SlideYDownTransition } from 'vue2-transitions';
import { extend } from 'vee-validate';
import { email, max, min } from 'vee-validate/dist/rules';
import { urlRegex } from '@/utils/regex';

extend('email', {
  ...email,
  message: (fieldName) => `Polje "${fieldName}" zahtijeva validnu email adresu`,
});

extend('hasSmallLetter', {
  validate(value) {
    return value.match(/(?=[a-z])/);
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" zahtijeva jedno malo slovo`,
});

extend('website', {
  validate(value) {
    return value.match(urlRegex);
  },
  message: (fieldName) => `Polje "${fieldName}" zahtijeva validan URL`,
});

extend('hasCapitalLetter', {
  validate(value) {
    return value.match(/(?=[A-Z])/);
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" zahtijeva jedno veliko slovo`,
});

extend('hasNumber', {
  validate(value) {
    return value.match(/(?=\d)/);
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" zahtijeva jednu cifru`,
});

extend('regex', {
  validate(value, { regex }) {
    return value.match(regex);
  },
  params: ['regex'],
  computesRequired: true,
  message: (fieldName, { regex }) =>
    `Polje "${fieldName}" ne podlaže pravilima ${regex}`,
});

extend('min_value', {
  validate(value, { min }) {
    return value >= min;
  },
  params: ['min'],
  computesRequired: true,
  message: (fieldName, { min }) =>
    `Polje "${fieldName}" zahtijeva broj veći ili jednak od ${min}`,
});
extend('max_value', {
  validate(value, { max }) {
    return value <= max;
  },
  params: ['max'],
  computesRequired: true,
  message: (fieldName, { max }) =>
    `Polje "${fieldName}" zahtijeva broj manji ili jednak od ${max}`,
});
extend('min', {
  ...min,
  message: (fieldName, { length }) =>
    `Polje "${fieldName}" zahtijeva minimum ${length} karaktera`,
});
extend('max', {
  ...max,
  message: (fieldName, { length }) =>
    `Polje "${fieldName}" zahtijeva maximum ${length} karaktera`,
});
extend('numeric', {
  validate(value) {
    return typeof value === 'number';
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" zahtijeva broj`,
});
extend('required', {
  validate(value) {
    return !['', null, undefined].includes(value);
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" je prazno`,
});

extend('between', {
  validate(value, { min, max }) {
    return value && value.length >= min && value.length <= max;
  },
  params: ['min', 'max'],
  computesRequired: true,
  message: (fieldName, { min, max }) =>
    `Polje "${fieldName}" zahtijeva između ${min} i ${max} karaktera`,
});

extend('positive', {
  validate(value) {
    return value && value > 0;
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" mora biti pozitivan broj`,
});

extend('non_negative', {
  validate(value) {
    return value >= 0;
  },
  computesRequired: true,
  message: (fieldName) => `Polje "${fieldName}" ne dozvoljava negativan broj`,
});

export default {
  components: {
    SlideYDownTransition,
  },
  computed: {
    moddedRules() {
      if (Array.isArray(this.rules)) {
        return this.rules.filter((rule) => rule).join('|');
      }
      return this.rules;
    },
  },
  props: {
    name: String,
    rules: [String, Array],
    className: String,
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/md/_variables.scss';
.tooltip-container {
  position: relative;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  max-width: 240px;
  background: rgb(196, 97, 95);
  background: linear-gradient(
    144deg,
    rgb(179, 74, 72) 34%,
    rgba(169, 68, 66, 1) 43%,
    rgba(136, 51, 49, 1) 87%
  );
  color: #fff;
  text-align: center;
  border: 1px solid white;
  border-radius: 6px;
  padding: 0.7em 2em;
  position: absolute;
  z-index: 100000;
  bottom: 100%;
  right: 0px;
  font-weight: 400;
}

.tooltip-container .tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 5%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: $state-danger-text $state-danger-text transparent transparent;
}

.tooltip-container:hover .tooltip-text,
.tooltip-container .md-input:focus ~ .tooltip-text {
  visibility: visible;
}
</style>
